/**
 * Shared pieces for the pages that show MARKET DATA from the public Market
 * Intel API (Heat Map, Lane Prices).
 *
 * Two things every such figure carries, per ADR-033 §4.6: which tier answered
 * (an r2 area is ~87,000 km² and must never look as precise as an r4 one) and
 * how old the snapshot is (a rate without its age reads as live).
 */

import Chip from '@mui/joy/Chip'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import {
    formatEquipmentCode,
    formatMarketIntelRate,
    formatMarketIntelRateCeiling,
    formatMarketIntelTierArea,
    formatMarketIntelTotal,
    type MarketIntelProvenance,
    type MarketIntelTier,
} from '@numeo/types-core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { marketIntelApi } from 'api/marketIntel'
import { useLoadPipelineData } from './shared'

dayjs.extend(relativeTime)

/** Offered until the snapshot's own list arrives, and if it cannot be read. */
const FALLBACK_EQUIPMENT = ['V', 'R', 'F', 'SD', 'DD', 'PO', 'LB', 'RG']

/** `RG · Removable Gooseneck` — the code operators search by, then what it means. */
const equipmentLabel = (code: string): string => {
    const name = formatEquipmentCode(code).replace(/^[A-Z0-9]+ - /, '')
    return name && name !== code ? `${code} · ${name}` : code
}

/**
 * The equipment picker for market-data tabs: every code with data in the snapshot (59 in prod, not a
 * hand-kept eight), labelled from the canonical catalogue.
 */
export function EquipmentSelect({ value, onChange }: { value: string; onChange: (code: string) => void }) {
    const { data, error } = useLoadPipelineData(() => marketIntelApi.getEquipment(), [])
    const codes = data?.equipment.length ? data.equipment : FALLBACK_EQUIPMENT
    const options = codes.includes(value) ? codes : [value, ...codes]
    return (
        <>
            <Select size="sm" value={value} onChange={(_, v) => v && onChange(v)} sx={{ minWidth: 220 }}>
                {options.map((code) => (
                    <Option key={code} value={code}>
                        {equipmentLabel(code)}
                    </Option>
                ))}
            </Select>
            {error ? (
                <Chip size="sm" variant="soft" color="warning" title={error instanceof Error ? error.message : String(error)}>
                    equipment list unavailable — showing common types
                </Chip>
            ) : null}
        </>
    )
}

export function money(v: number | null | undefined): string {
    return v == null ? '—' : formatMarketIntelRate(v)
}

/** A median total price: whole dollars, the open bands as `under $200` and `$15,000+` (ADR-033 §3.1). */
export function totalMoney(v: number | null | undefined): string {
    return v == null ? '—' : formatMarketIntelTotal(v)
}

/** A max at the ceiling means "that or more", not that exact figure. */
export function maxMoney(v: number | null): string {
    return v == null ? '—' : formatMarketIntelRateCeiling(v)
}

/** "14d window · built 23 min ago", or a warning when this instance holds no snapshot yet. */
export function SnapshotAge({ provenance }: { provenance: MarketIntelProvenance }) {
    if (!provenance.builtAt) {
        return (
            <Chip size="sm" variant="soft" color="warning">
                no snapshot on this instance yet
            </Chip>
        )
    }
    return (
        <Chip size="sm" variant="soft" title={new Date(provenance.builtAt).toLocaleString()}>
            {provenance.window ?? '—'} window · built {dayjs(provenance.builtAt).fromNow()}
        </Chip>
    )
}

export function TierChip({ tier }: { tier: MarketIntelTier }) {
    return (
        <Chip size="sm" variant="soft" color={tier === 'r4' ? 'primary' : 'neutral'}>
            {tier} · {formatMarketIntelTierArea(tier)}
        </Chip>
    )
}
